import { AgsHeaderComponent } from "ags/components/ags-header-component";
import { AgsPreloaderComponent } from "ags/components/ags-preloader-component";
import { AgsPromptComponent } from "ags/components/ags-prompt-component";
import { AgsCurrencyService } from "ags/services/ags-currency";
import { AgsSoundService } from "ags/services/ags-sound-service";
import { BaseSetup } from "appworks/boot/base-setup";
import { Components } from "appworks/components/components";
import { ComponentFactory } from "appworks/components/factories/component-factory";
import { gameLoop } from "appworks/core/game-loop";
import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { LoaderService } from "appworks/loader/loader-service";
import { commsManager } from "appworks/server/comms-manager";
import { SocketConnector } from "appworks/server/connectors/socket-connector";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { Contract } from "appworks/utils/contracts/contract";
import { deviceInfo } from "appworks/utils/device-info";
import { AutoplayClickDetector } from "appworks/utils/geom/autoplay-click-detector";
import { Point } from "appworks/utils/geom/point";
import { logger } from "appworks/utils/logger";
import { Timer } from "appworks/utils/timer";
import { gameiom } from "gameiom/gameiom";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { patchInDebugWrapperFunctions } from "gameiom/wrapper/debug-wrapper";
import { injectWrapper } from "gameiom/wrapper/lib/game-wrapper";
import { wrapperImplementation } from "gameiom/wrapper/wrapper-implementation";
import { AlertComponent } from "slotworks/components/alert/alert-component";
import { CelebrateWinComponent } from "slotworks/components/celebrate-win/celebrate-win-component";
import { GenericComponentFactory } from "slotworks/components/factories/generic-component-factory";
import { SymbolFooterComponentFactory } from "slotworks/components/factories/symbol-footer-factory";
import { AnticipationSkipMode, SpinReelTransition } from "slotworks/components/matrix/reel/transition-behaviours/spin/spin-reel-transition";
import { AgsBetBarComponent } from "../components/ags-bet-bar-component";
import { AgsJackpotDisplayComponent } from "../components/ags-jackpot-display-component";
import { AnimateWinsComponent } from "../components/animate-wins-component";
import { LoopWinsComponent } from "../components/loop-wins-component";
import { agsSocketConnector } from "../integration/connector/ags-socket-connector";
import { AgsTransactionService } from "../services/ags-transaction";
import { AgsTranslationsService } from "../services/ags-translation";
import { AgsMatrixFactory } from "./factories/ags-matrix-factory";
import { AgsSetupUI } from "./factories/ags-setup-ui";
import { setupSharedI18N } from "./factories/ags-shared-i18n";
import { AgsSoundSetup } from "./factories/ags-sounds";

declare const __DEBUG__: boolean;

export interface AgsOptions {
    gameId: number;
    providerId?: number;
    ways: boolean;
}

export class AgsSetup extends BaseSetup {
    protected options: AgsOptions;
    protected componentFactories: Map<string, ComponentFactory>;

    constructor(options: AgsOptions) {
        super();
        this.options = { providerId: 48, ...options };

        if (__DEBUG__) {
            patchInDebugWrapperFunctions();
        }
    }

    public setupCanvas(): void {
        super.setupCanvas();

        const canvasService = Services.get(CanvasService);
        AutoplayClickDetector.init();

        canvasService.cssCanvasEnabled = true;
        canvasService.setPortraitEnabled(!deviceInfo.isDesktop);
        canvasService.setPortraitCanvasEnabled(!deviceInfo.isDesktop);

        // Remove bottom black bar by scaling up the game
        if (!deviceInfo.isDesktop) {
            let lastWidth = 0, lastHeight = 0;

            Timer.setInterval(() => {
                if (deviceInfo.getWindowWidth() === lastWidth &&
                    deviceInfo.getWindowHeight() === lastHeight) { return; }

                let scale = 1.16;
                while (scale > 1) {
                    const width = canvasService.canvas.clientWidth * scale;

                    if (width > deviceInfo.getWindowWidth()) {
                        scale -= 0.01;
                    } else {
                        break;
                    }
                }
                canvasService.setLandscapeScale(scale, new Point(0.5, 0));

                lastWidth = deviceInfo.getWindowWidth();
                lastHeight = deviceInfo.getWindowHeight();
            }, 0);
        }
    }

    public setupComms(): void {
        super.setupComms();

        SocketConnector.REQUEST_TIMEOUT = 15000;
        commsManager.connector = agsSocketConnector;

        injectWrapper();
        wrapperImplementation.init(__DEBUG__);
    }

    public setupComponents(): void {
        super.setupComponents();
        SpinReelTransition.anticipationSkipMode = AnticipationSkipMode.NEVER;

        this.componentFactories = new Map<string, ComponentFactory>([
            ["AgsMatrixFactory", new AgsMatrixFactory()],
            ["AgsBetBarComponent", new GenericComponentFactory(AgsBetBarComponent)],
            ["SymbolFooterComponentFactory", new SymbolFooterComponentFactory(1.5, 0.15, -0.35)],
            ["AgsJackpotDisplayComponent", new GenericComponentFactory(AgsJackpotDisplayComponent)],
            ["AnimateWinsComponent", new GenericComponentFactory(AnimateWinsComponent, false)],
            ["LoopWinsComponent", new GenericComponentFactory(LoopWinsComponent)],
            ["AgsHeaderComponent", new GenericComponentFactory(AgsHeaderComponent)],
            ["CelebrateWinComponent", new GenericComponentFactory(CelebrateWinComponent)]
        ]);

        this.componentFactories.forEach((componentFactory) => {
            Components.register(componentFactory);
        });

        Components.register(new GenericComponentFactory(AlertComponent), 0);
        Components.register(new GenericComponentFactory(AgsPromptComponent, { usePromptLayerAsClickListener: true }), 0);
        Components.register(new GenericComponentFactory(AgsPreloaderComponent), 0);
    }

    public setupDebug(): void {
        if (!gameiomModel.enableForcing) {
            return;
        }

        super.setupDebug();

        if (__DEBUG__) {
            gameLoop.showStats();
            logger.setLevel(logger.INFO);
        }
    }

    public setupI18N(): void {
        setupSharedI18N();

        if (!gameiomModel.displayMaxBet) {
            Services.get(TranslationsService).setAlias("fot.new", "adjust_bet_no_max");
            Services.get(TranslationsService).setAlias("fot.no", "adjust_bet_no_max");
        }
    }

    public setupAsync(): Contract<void> {
        return new Contract((resolve) => {
            gameiom.init().then(() => {
                gameiom.setProviderId(this.options.providerId);

/////////////////////////////////
///////////////////////////////////////////////////////////////////
//////////////////////////////
                    gameiom.initWrapper();
                    resolve();
/////////////////////////////////////
///////////////////
//////////////////////////
            });
        });
    }

    public setupServices(): void {
        super.setupServices();

        Services.deregister(TransactionService);
        Services.register(new AgsTransactionService());

        Services.deregister(TranslationsService);
        Services.register(new AgsTranslationsService());

        Services.deregister(CurrencyService);
        Services.register(new AgsCurrencyService());

        Services.deregister(SoundService);
        Services.register(new AgsSoundService());
    }

    public setupSound(): void {
        super.setupSound();

        AgsSoundSetup();

        Services.get(LoaderService).loadSoundsInPreloader = true;
    }

    public setupUI(): void {
        AgsSetupUI();
    }
}
